import type { SchoolClassVisibilities } from "../enums/schoolClasses";

// Both legs treat a rejection exactly like a resolved false: neither confirms the write.
const _resolveFalseOnError = async (run: () => boolean | Promise<boolean>): Promise<boolean> => {
    try {
        return await run();
    } catch {
        return false;
    }
};

export interface CreateClassAndAssignParams {
    name: string;
    visibility: SchoolClassVisibilities;
    createSchoolClass: (name: string, visibility: SchoolClassVisibilities) => Promise<string | null>;
    assignSchoolClass: (schoolClassId: string) => boolean | Promise<boolean>;
    // Puts the activity verifiably back on its previous class (or none). A failed assignSchoolClass can
    // still have landed backend-side (e.g. a timeout after the write), and deleting a class cascade-deletes
    // its activities — so the created class may only be deleted once this confirms the activity is off it:
    // an orphaned empty class is recoverable, a cascade-deleted activity is not. Same invariant as
    // executeShareWithClassRollback (ShareFlow.ts).
    detachActivity: () => boolean | Promise<boolean>;
    deleteSchoolClass: (schoolClassId: string) => Promise<boolean>;
}

// Create a class inline and assign the activity to it, deleting the fresh class again when the assignment
// fails — an orphan class would otherwise stay behind, and its name then trips the duplicate-name check,
// so a retry from the same popup could never be confirmed. Resolves whether the assignment landed.
// A failed create needs no cleanup and reports its own error already.
export const createClassAndAssign = async (params: CreateClassAndAssignParams): Promise<boolean> => {
    const createdSchoolClassId = await params.createSchoolClass(params.name, params.visibility);
    if (!createdSchoolClassId) return false;

    if (await _resolveFalseOnError(() => params.assignSchoolClass(createdSchoolClassId))) return true;

    const isActivityDetached = await _resolveFalseOnError(params.detachActivity);
    const rolledBack =
        isActivityDetached && (await _resolveFalseOnError(() => params.deleteSchoolClass(createdSchoolClassId)));
    if (!rolledBack) {
        console.warn("Inline-created class was not removed after a failed assignment", { createdSchoolClassId });
    }

    return false;
};
